import { FC, useCallback } from 'react';
import { useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import useDeclarations from 'hooks/useDeclarations';
import Notification from 'components/ui/base/notification/Notification';
import FormTextArea from 'components/ui/composed/formTextArea/FormTextArea';
import FormCheckbox from 'components/ui/composed/formCheckbox/FormCheckbox';
import { getRequiredMessage } from 'views/declarations/utils/validation-utils';

const nctsFormValidation = Yup.object().shape({
    justification: Yup.string().required(getRequiredMessage('justification')),
});

interface Props {
    onCancel: () => void;
}

const InvalidateFormNcts: FC<Props> = ({ onCancel }) => {
    const { invalidateNctsDeclaration, error, isLoading, declaration } = useDeclarations();
    const { t } = useTranslation('customs_declarations');

    const formik = useFormik<{
        decision: boolean;
        justification: string;
        initiatedByCustoms: boolean;
    }>({
        initialValues: {
            decision: false,
            justification: '',
            initiatedByCustoms: false,
        },
        validationSchema: nctsFormValidation,
        validateOnMount: true,
        enableReinitialize: true,
        onSubmit: (values) => {
            handleNctsSubmit(values);
        },
        onReset: () => {
            onCancel();
        },
    });

    const handleNctsInvalidate = useCallback(
        async (decision: boolean, justification: string, initiatedByCustoms: boolean) => {
            if (!(declaration?.id && declaration?.declarationInternalType)) return;
            const res = await invalidateNctsDeclaration(declaration.id, decision, justification, initiatedByCustoms);
            if (res && !error && !isLoading) {
                onCancel();
                Notification({
                    type: 'success',
                    messageTitle: t('invalidate.invalidate-success-title'),
                    description: t('invalidate.invalidate-success-message'),
                    placement: 'top',
                });
                formik.resetForm();
            } else {
                Notification({
                    type: 'error',
                    messageTitle: t('invalidate.invalidate-error-title'),
                    description: t('invalidate.invalidate-error-message'),
                    placement: 'top',
                });
            }
        },
        [
            declaration?.id,
            onCancel,
            t,
            error,
            isLoading,
            invalidateNctsDeclaration,
            declaration?.declarationInternalType,
            formik,
        ]
    );

    const handleNctsSubmit = useCallback(
        async (values: { decision: boolean; justification: string; initiatedByCustoms: boolean }) => {
            await handleNctsInvalidate(values.decision, values.justification, values.initiatedByCustoms);
        },
        [handleNctsInvalidate]
    );

    return (
        <FormikProvider value={formik}>
            <form id="invalidateForm" onSubmit={formik.handleSubmit} onReset={formik.handleReset}>
                <FormTextArea
                    label="Justification"
                    required
                    fieldProps={formik.getFieldProps('justification')}
                    fieldMeta={formik.getFieldMeta('justification')}
                    placeholder="Enter invalidation justification"
                />
                <FormCheckbox
                    label="Decision"
                    fieldProps={formik.getFieldProps('decision')}
                    fieldMeta={formik.getFieldMeta('decision')}
                />
                <FormCheckbox
                    label="Initiated by Customs"
                    fieldProps={formik.getFieldProps('initiatedByCustoms')}
                    fieldMeta={formik.getFieldMeta('initiatedByCustoms')}
                />
            </form>
        </FormikProvider>
    );
};

export default InvalidateFormNcts;
